import { useEffect, useState, memo, useMemo, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Typography, Box, IconButton,
  FormControlLabel, Radio, RadioGroup, Paper, ClickAwayListener,
  Snackbar, Alert, CircularProgress, Divider
} from '@mui/material';
import { Close, SwapHoriz } from '@mui/icons-material';

const PRIMARY = '#5c3d1e';
const PRIMARY_DARK = '#3b2410';

const fs = {
  '& .MuiOutlinedInput-root': { borderRadius: '4px', height: '32px' },
  '& .MuiInputLabel-root': { fontSize: '13px' },
  '& .MuiInputBase-input': { padding: '6px 10px', fontSize: '13px' },
};

// AcType: 1=Master(Party), 2=AccountHead(Other), 3=Bank
const ACTYPE = { Party: 1, Other: 2, Bank: 3 };
const ACTYPE_R = { 1: 'Party', 2: 'Other', 3: 'Bank' };

const EMPTY = {
  Id: 0,
  BillNo: '',
  BillDate: '',
  IsTypeCredit: true,       // true=Credit, false=Debit
  // Credit side
  AcTypeCredit: 1,
  CreditMasterId: null,
  CreditAccountHeadId: null,
  CreditBankId: null,
  // Debit side
  AcTypeDebit: 1,
  DebitMasterId: null,
  DebitAccountHeadId: null,
  DebitBankId: null,
  Amount: '',
  IsCancelled: false,
  Remarks: '',
};

const sxDialogTitle = { py: 1, px: 1.5, background: `linear-gradient(135deg, ${PRIMARY_DARK}, ${PRIMARY})`, color: 'white' };
const sxDialogContent = { p: 1.5, background: '#fdf6ec' };
const sxInnerBox = { background: '#fff', borderRadius: '8px', p: 1.5, border: '1px solid #f0e8d8' };
const sxActions = { px: 1.5, py: 1, background: '#f8f9fa', borderTop: '1px solid #e9ecef', justifyContent: 'flex-end' };
const sxSectionBox = { p: 1.5, border: '1px solid #e5e7eb', borderRadius: '6px', background: '#fafafa' };
const sxRemarks = {
  '& .MuiInputLabel-root': { fontSize: '13px' },
  '& .MuiInputBase-input': { fontSize: '13px', padding: '6px 10px' },
  '& .MuiOutlinedInput-root': { borderRadius: '4px' },
};

// Searchable dropdown with local filtering
const SearchableSelect = memo(({ label, value, options, onChange, onBlur, error, helperText, disabled }) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const selectedLabel = options.find(o => o.Id === value)?.Name || '';

  const filtered = useMemo(() => {
    if (!search.trim()) return options.slice(0, 50);
    const q = search.toLowerCase();
    return options.filter(o => o.Name?.toLowerCase().includes(q)).slice(0, 50);
  }, [options, search]);

  const handleSelect = (id) => { onChange(id); setSearch(''); setOpen(false); };
  const handleOpen = () => { if (!disabled) { setSearch(''); setOpen(true); } };

  return (
    <ClickAwayListener onClickAway={() => { setOpen(false); setSearch(''); if (onBlur) onBlur(); }}>
      <Box ref={containerRef} sx={{ position: 'relative' }}>
        <TextField
          fullWidth size="small"
          label={label}
          value={open ? search : selectedLabel}
          onChange={e => setSearch(e.target.value)}
          onClick={handleOpen}
          onFocus={handleOpen}
          disabled={disabled}
          error={error}
          helperText={helperText}
          placeholder={open ? 'Type to search...' : ''}
          autoComplete="off"
          sx={{ ...fs, '& .MuiInputBase-input': { padding: '6px 10px', fontSize: '13px', cursor: disabled ? 'default' : 'pointer' } }}
        />
        {open && (
          <Paper elevation={4} sx={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1400, maxHeight: 220, overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: '4px', mt: '2px' }}>
            {filtered.length === 0
              ? <Box sx={{ p: 1.5, color: '#999', fontSize: '13px', textAlign: 'center' }}>No results found</Box>
              : filtered.map(o => (
                <Box key={o.Id} onMouseDown={e => { e.preventDefault(); handleSelect(o.Id); }}
                  sx={{ px: 1.5, py: 0.75, fontSize: '13px', cursor: 'pointer', backgroundColor: o.Id === value ? 'rgba(92,61,30,0.08)' : 'transparent', fontWeight: o.Id === value ? 600 : 400, '&:hover': { backgroundColor: 'rgba(92,61,30,0.06)' } }}>
                  {o.Name}
                </Box>
              ))
            }
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );
});

const CreditDebitModal = memo(({
  open,
  onClose,
  modalAction,
  initialData,
  onSave,
  dropdownData,   // { Party: [], Other: [], Bank: [] }
  saving,
  fetchMaxBillNo,
}) => {
  const [form, setForm] = useState(EMPTY);
  const [creditType, setCreditType] = useState('Party');
  const [debitType, setDebitType] = useState('Party');
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const isView = modalAction === 'view';

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setTouched({});

    if (modalAction === 'create') {
      const today = new Date().toISOString().split('T')[0];
      setForm({ ...EMPTY, BillDate: today });
      setCreditType('Party');
      setDebitType('Party');
      if (fetchMaxBillNo) {
        fetchMaxBillNo().then(d => setForm(prev => ({ ...prev, BillNo: d.billNumber || '' }))).catch(() => {});
      }
    } else if (initialData) {
      setForm({
        ...EMPTY,
        ...initialData,
        BillDate: initialData.BillDate ? initialData.BillDate.split('T')[0] : '',
      });
      setCreditType(ACTYPE_R[initialData.AcTypeCredit] || 'Party');
      setDebitType(ACTYPE_R[initialData.AcTypeDebit] || 'Party');
    }
  }, [open, modalAction]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  // Credit side account type change
  const handleCreditTypeChange = (type) => {
    setCreditType(type);
    setForm(prev => ({ ...prev, AcTypeCredit: ACTYPE[type], CreditMasterId: null, CreditAccountHeadId: null, CreditBankId: null }));
  };

  // Debit side account type change
  const handleDebitTypeChange = (type) => {
    setDebitType(type);
    setForm(prev => ({ ...prev, AcTypeDebit: ACTYPE[type], DebitMasterId: null, DebitAccountHeadId: null, DebitBankId: null }));
  };

  const handleCreditAccountChange = (id) => {
    setForm(prev => ({
      ...prev,
      AcTypeCredit: ACTYPE[creditType],
      CreditMasterId: creditType === 'Party' ? id : null,
      CreditAccountHeadId: creditType === 'Other' ? id : null,
      CreditBankId: creditType === 'Bank' ? id : null,
    }));
    if (errors.creditAccount) setErrors(prev => ({ ...prev, creditAccount: '' }));
  };

  const handleDebitAccountChange = (id) => {
    setForm(prev => ({
      ...prev,
      AcTypeDebit: ACTYPE[debitType],
      DebitMasterId: debitType === 'Party' ? id : null,
      DebitAccountHeadId: debitType === 'Other' ? id : null,
      DebitBankId: debitType === 'Bank' ? id : null,
    }));
    if (errors.debitAccount) setErrors(prev => ({ ...prev, debitAccount: '' }));
  };

  const selectedCreditId = form.CreditMasterId || form.CreditAccountHeadId || form.CreditBankId || null;
  const selectedDebitId = form.DebitMasterId || form.DebitAccountHeadId || form.DebitBankId || null;

  const creditList = (dropdownData && dropdownData[creditType]) || [];
  const debitList = (dropdownData && dropdownData[debitType]) || [];

  const validate = (field, value) => {
    if (field === 'BillNo' && (!value || !String(value).trim())) return 'Required';
    if (field === 'BillDate' && !value) return 'Required';
    if (field === 'creditAccount' && !value) return 'Required';
    if (field === 'debitAccount' && !value) return 'Required';
    if (field === 'Amount' && (!value || parseFloat(value) <= 0)) return 'Must be > 0';
    return '';
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    const val = field === 'creditAccount' ? selectedCreditId : field === 'debitAccount' ? selectedDebitId : form[field];
    setErrors(prev => ({ ...prev, [field]: validate(field, val) }));
  };

  const handleSave = () => {
    const errs = {};
    ['BillNo', 'BillDate', 'creditAccount', 'debitAccount', 'Amount'].forEach(f => {
      const val = f === 'creditAccount' ? selectedCreditId : f === 'debitAccount' ? selectedDebitId : form[f];
      const e = validate(f, val);
      if (e) errs[f] = e;
    });
    setTouched({ BillNo: true, BillDate: true, creditAccount: true, debitAccount: true, Amount: true });
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      setSnackbar({ open: true, message: Object.values(errs).join(' | ') });
      return;
    }
    onSave(form);
  };

  const title = modalAction === 'create' ? 'Credit Debit - Create'
    : modalAction === 'edit' ? 'Credit Debit - Edit'
    : 'Credit Debit - View';

  const creditSelectedName = creditList.find(m => m.Id === selectedCreditId)?.Name || '-';
  const debitSelectedName = debitList.find(m => m.Id === selectedDebitId)?.Name || '-';

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '8px', maxWidth: '860px' } }}>

        {/* Header */}
        <DialogTitle sx={sxDialogTitle}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SwapHoriz sx={{ fontSize: 18 }} />
              <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>{title}</Typography>
            </Box>
            <IconButton onClick={onClose} size="small" sx={{ color: 'white', p: '2px' }}>
              <Close style={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={sxDialogContent}>
          <Box sx={sxInnerBox}>

            {/* Top row: Bill No, Date, Type */}
            <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
              <Grid item xs={6} sm={2.5}>
                <TextField fullWidth size="small"
                  label="Bill No. *"
                  value={form.BillNo}
                  onChange={e => set('BillNo', e.target.value)}
                  onBlur={() => handleBlur('BillNo')}
                  disabled={isView}
                  error={touched.BillNo && !!errors.BillNo}
                  helperText={touched.BillNo && errors.BillNo}
                  sx={fs}
                />
              </Grid>

              <Grid item xs={6} sm={2.5}>
                <TextField fullWidth size="small" type="date"
                  label="Date *"
                  value={form.BillDate}
                  onChange={e => set('BillDate', e.target.value)}
                  onBlur={() => handleBlur('BillDate')}
                  disabled={isView}
                  error={touched.BillDate && !!errors.BillDate}
                  helperText={touched.BillDate && errors.BillDate}
                  InputLabelProps={{ shrink: true }}
                  sx={fs}
                />
              </Grid>

              {/* Transaction Type */}
              <Grid item xs={12} sm={3}>
                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '32px' }}>
                  <Typography sx={{ fontSize: '10px', color: '#888', mb: 0.25, lineHeight: 1 }}>Transaction Type</Typography>
                  <RadioGroup row value={form.IsTypeCredit ? 'credit' : 'debit'}
                    onChange={e => set('IsTypeCredit', e.target.value === 'credit')}
                    sx={{ gap: 1 }}>
                    {[
                      { value: 'credit', label: 'Credit Note' },
                      { value: 'debit', label: 'Debit Note' },
                    ].map(t => (
                      <FormControlLabel key={t.value} value={t.value}
                        disabled={isView}
                        control={<Radio size="small" sx={{
                          color: t.value === 'credit' ? '#2e7d32' : '#c62828',
                          '&.Mui-checked': { color: t.value === 'credit' ? '#2e7d32' : '#c62828' },
                          p: '2px'
                        }} />}
                        label={<Typography sx={{
                          fontSize: '12px', fontWeight: 600,
                          color: (form.IsTypeCredit ? 'credit' : 'debit') === t.value
                            ? (t.value === 'credit' ? '#2e7d32' : '#c62828')
                            : '#666'
                        }}>{t.label}</Typography>}
                        sx={{ margin: 0 }}
                      />
                    ))}
                  </RadioGroup>
                </Box>
              </Grid>

              {/* Amount */}
              <Grid item xs={6} sm={2.5}>
                <TextField fullWidth size="small" type="number"
                  label="Amount *"
                  value={form.Amount}
                  onChange={e => set('Amount', e.target.value)}
                  onBlur={() => handleBlur('Amount')}
                  disabled={isView}
                  error={touched.Amount && !!errors.Amount}
                  helperText={touched.Amount && errors.Amount}
                  inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                  sx={fs}
                />
              </Grid>
            </Grid>

            {/* Credit & Debit sections side by side */}
            <Grid container spacing={1.5}>

              {/* Credit Section */}
              <Grid item xs={12} sm={6}>
                <Box sx={{
                  ...sxSectionBox,
                  borderLeft: `3px solid #4caf50`,
                  background: form.IsTypeCredit ? '#f0faf0' : '#fafafa',
                  transition: 'background 0.2s',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography sx={{ fontWeight: 700, color: '#2e7d32', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Credit
                    </Typography>
                    {form.IsTypeCredit && (
                      <Typography sx={{ fontSize: '10px', color: '#fff', background: '#2e7d32', borderRadius: '3px', px: 0.75, py: 0.1, fontWeight: 600 }}>
                        Primary
                      </Typography>
                    )}
                  </Box>
                  {/* AcType radio */}
                  {!isView ? (
                    <RadioGroup row value={creditType} onChange={e => handleCreditTypeChange(e.target.value)} sx={{ gap: 0.5, mb: 1 }}>
                      {['Party', 'Other', 'Bank'].map(t => (
                        <FormControlLabel key={t} value={t}
                          control={<Radio size="small" sx={{ color: PRIMARY, '&.Mui-checked': { color: PRIMARY }, p: '2px' }} />}
                          label={<Typography sx={{ fontSize: '11px', fontWeight: 500 }}>{t}</Typography>}
                          sx={{ margin: 0 }}
                        />
                      ))}
                    </RadioGroup>
                  ) : (
                    <Typography sx={{ fontSize: '11px', color: '#666', mb: 1 }}>{creditType}</Typography>
                  )}

                  {isView ? (
                    <TextField fullWidth size="small" label={creditType} value={creditSelectedName} disabled sx={fs} />
                  ) : (
                    <SearchableSelect
                      label={`${creditType} *`}
                      value={selectedCreditId}
                      options={creditList}
                      onChange={handleCreditAccountChange}
                      onBlur={() => handleBlur('creditAccount')}
                      error={touched.creditAccount && !!errors.creditAccount}
                      helperText={touched.creditAccount && errors.creditAccount}
                    />
                  )}
                </Box>
              </Grid>

              {/* Debit Section */}
              <Grid item xs={12} sm={6}>
                <Box sx={{
                  ...sxSectionBox,
                  borderLeft: `3px solid #f44336`,
                  background: !form.IsTypeCredit ? '#fff5f5' : '#fafafa',
                  transition: 'background 0.2s',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography sx={{ fontWeight: 700, color: '#c62828', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Debit
                    </Typography>
                    {!form.IsTypeCredit && (
                      <Typography sx={{ fontSize: '10px', color: '#fff', background: '#c62828', borderRadius: '3px', px: 0.75, py: 0.1, fontWeight: 600 }}>
                        Primary
                      </Typography>
                    )}
                  </Box>
                  {/* AcType radio */}
                  {!isView ? (
                    <RadioGroup row value={debitType} onChange={e => handleDebitTypeChange(e.target.value)} sx={{ gap: 0.5, mb: 1 }}>
                      {['Party', 'Other', 'Bank'].map(t => (
                        <FormControlLabel key={t} value={t}
                          control={<Radio size="small" sx={{ color: PRIMARY, '&.Mui-checked': { color: PRIMARY }, p: '2px' }} />}
                          label={<Typography sx={{ fontSize: '11px', fontWeight: 500 }}>{t}</Typography>}
                          sx={{ margin: 0 }}
                        />
                      ))}
                    </RadioGroup>
                  ) : (
                    <Typography sx={{ fontSize: '11px', color: '#666', mb: 1 }}>{debitType}</Typography>
                  )}

                  {isView ? (
                    <TextField fullWidth size="small" label={debitType} value={debitSelectedName} disabled sx={fs} />
                  ) : (
                    <SearchableSelect
                      label={`${debitType} *`}
                      value={selectedDebitId}
                      options={debitList}
                      onChange={handleDebitAccountChange}
                      onBlur={() => handleBlur('debitAccount')}
                      error={touched.debitAccount && !!errors.debitAccount}
                      helperText={touched.debitAccount && errors.debitAccount}
                    />
                  )}
                </Box>
              </Grid>
            </Grid>

            {/* Remarks */}
            <Box sx={{ mt: 1.5 }}>
              <TextField fullWidth multiline rows={2} label="Remarks"
                value={form.Remarks || ''}
                onChange={e => set('Remarks', e.target.value)}
                disabled={isView}
                inputProps={{ maxLength: 100 }}
                sx={sxRemarks}
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={sxActions}>
          <Button onClick={onClose} style={{ color: '#6c757d', textTransform: 'none', fontWeight: 500, fontSize: '12px' }}>
            Cancel
          </Button>
          {!isView && (
            <Button onClick={handleSave} variant="contained" disabled={saving}
              style={{ background: 'linear-gradient(135deg, #c9a84c, #9a7a2e)', color: 'white', textTransform: 'none', fontWeight: 600, fontSize: '12px', borderRadius: '4px' }}>
              {saving ? <CircularProgress size={14} sx={{ color: 'white' }} /> : modalAction === 'create' ? 'Save' : 'Update'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar(p => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setSnackbar(p => ({ ...p, open: false }))} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
});

export default CreditDebitModal;
